import React, { FocusEventHandler, ReactElement, useEffect, useMemo, useRef } from 'react';
import { InitialConfigType, LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ClickableLinkPlugin } from '@lexical/react/LexicalClickableLinkPlugin';
import { ListNode, ListItemNode } from '@lexical/list';
import { LinkNode } from '@lexical/link';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import {
  $createParagraphNode,
  $getRoot,
  $isElementNode,
  $isTextNode,
  LexicalNode,
  ParagraphNode,
  RootNode,
} from 'lexical';
import { LexicalToolbar } from './LexicalToolbar';
import i18next from 'i18next';
import EditablePlugin from './EditablePlugin';

type LexicalEditorProps = {
  value: string;
  placeholder?: string;
  onChange: (html: string) => void;
  onBlur?: FocusEventHandler<HTMLDivElement>;
  hasError?: boolean;
  editable?: boolean;
};

function PlaceholderComponent({ placeholder }: { placeholder?: string }): ReactElement {
  return (
    <div className="editor-placeholder text-muted">
      {placeholder || i18next.t('generic.lexicalEditorPlaceholder')}
    </div>
  );
}

const EXTERNAL_SYNC_TAG = 'external-sync';

function toRootLevelNodes(nodes: LexicalNode[]): LexicalNode[] {
  const blocks: LexicalNode[] = [];

  for (const node of nodes) {
    if ($isElementNode(node) && !node.isInline()) {
      blocks.push(node);
      continue;
    }

    if ($isTextNode(node) && node.getTextContent().trim() === '') {
      continue;
    }

    const paragraph: ParagraphNode = $createParagraphNode();
    paragraph.append(node);
    blocks.push(paragraph);
  }

  return blocks;
}

function normalizeHtml(html: string): string {
  const plainText = html
    .replace(/<br\s*\/?>/gi, '')
    .replace(/&nbsp;/gi, '')
    .replace(/<[^>]+>/g, '')
    .trim();

  return plainText.length === 0 ? '' : html;
}

function ValueSyncPlugin({ value, onChange }: { value: string; onChange: (html: string) => void }): ReactElement {
  const [editor] = useLexicalComposerContext();
  const lastHtmlRef = useRef<string | null>(null);

  useEffect(() => {
    if (value === lastHtmlRef.current) {
      return;
    }

    lastHtmlRef.current = value;

    editor.update(
      () => {
        const root: RootNode = $getRoot();
        root.clear();

        if (!value) {
          return;
        }

        const wrappedHtml = value.startsWith('<') ? value : `<p>${value}</p>`;
        const parser: DOMParser = new DOMParser();
        const dom: Document = parser.parseFromString(wrappedHtml, 'text/html');
        const nodes: LexicalNode[] = $generateNodesFromDOM(editor, dom);
        root.append(...toRootLevelNodes(nodes));
      },
      { tag: EXTERNAL_SYNC_TAG }
    );
  }, [editor, value]);

  return (
    <OnChangePlugin
      ignoreSelectionChange
      onChange={(editorState, _editor, tags) => {
        if (tags.has(EXTERNAL_SYNC_TAG)) {
          return;
        }

        editorState.read(() => {
          const html = normalizeHtml($generateHtmlFromNodes(editor));
          lastHtmlRef.current = html;
          onChange(html);
        });
      }}
    />
  );
}

export default function LexicalEditor({ value, placeholder, onChange, onBlur, hasError, editable }: LexicalEditorProps): ReactElement {
  const isEditable: boolean = editable ?? true;

  const initialConfig: InitialConfigType = useMemo(() => ({
    namespace: 'WYSIWYGEditor',
    theme: {
      paragraph: 'mb-2',
      link: 'link-primary',
      text: {
        underline: 'text-decoration-underline',
        bold: 'fw-bold',
        italic: 'fst-italic',
      },
    },
    onError: (error: Error) => {
      console.error('Lexical error:', error);
    },
    editorState: null,
    nodes: [ListNode, ListItemNode, LinkNode],
    editable: isEditable,
  }), [isEditable]);

  return (
    <div 
      className={`p-3 border rounded ${hasError ? 'border-danger' : 'border'}`}>
      <LexicalComposer initialConfig={initialConfig}>
        <EditablePlugin editable={isEditable} />
        {isEditable && <LexicalToolbar />}
        <div
          className="position-relative border rounded p-2"
          onDragStart={(e) => e.preventDefault()}
        >
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className="form-control border-0 cursor-text"
                style={!isEditable ? { backgroundColor: '#e9ecef', color: '#6c757d' } : undefined}
                onBlur={onBlur}
                draggable={true}
                onDragStart={(e) => e.preventDefault()}
              />
            }
            placeholder={<PlaceholderComponent placeholder={placeholder} />}
            ErrorBoundary={LexicalErrorBoundary}
          />
          <HistoryPlugin />
          <ListPlugin />
          <LinkPlugin />
          <ClickableLinkPlugin newTab />
          <ValueSyncPlugin value={value} onChange={onChange} />
        </div>
      </LexicalComposer>
    </div>
  );
}
